import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Ruler, Scissors, Shirt, Package, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const HowItWorksPage = () => {
  const steps = [
    {
      number: "01",
      title: "Book Your Consultation",
      icon: Calendar,
      description: "Schedule your personalized appointment at our studio. This initial meeting is complimentary and allows us to understand your style preferences, needs, and timeline.",
      details: [
        "Choose from available appointment slots online",
        "Select the service type that interests you",
        "Receive confirmation and preparation tips via email",
        "Duration: approximately 30-45 minutes"
      ],
      image: "https://images.pexels.com/photos/5370706/pexels-photo-5370706.jpeg?w=800"
    },
    {
      number: "02",
      title: "Design & Selection",
      icon: Scissors,
      description: "Work with our expert consultants to design your perfect garment. Explore our fabric library, select styling details, and make every choice uniquely yours.",
      details: [
        "Browse 500+ premium fabrics from top mills",
        "Choose lapel style, buttons, pockets, and more",
        "Select lining, piping, and monogram options",
        "Review design mockups and finalize selections"
      ],
      image: "https://images.pexels.com/photos/4614227/pexels-photo-4614227.jpeg?w=800"
    },
    {
      number: "03",
      title: "Precision Measurements",
      icon: Ruler,
      description: "Our skilled tailors take over 30 precise measurements to ensure your garment fits perfectly. We capture every detail of your unique physique.",
      details: [
        "Comprehensive body measurements taken",
        "Posture and fit preferences noted",
        "Reference points for future orders saved",
        "Duration: approximately 20-30 minutes"
      ],
      image: "https://images.pexels.com/photos/6765350/pexels-photo-6765350.jpeg?w=800"
    },
    {
      number: "04",
      title: "Expert Craftsmanship",
      icon: Shirt,
      description: "Your garment enters production with our skilled artisans. Traditional techniques meet modern precision as your piece takes shape.",
      details: [
        "Pattern created specifically for you",
        "Hand-cut from your selected fabric",
        "Expertly constructed with attention to detail",
        "Production time: 6-10 weeks"
      ],
      image: "https://images.pexels.com/photos/6766232/pexels-photo-6766232.jpeg?w=800"
    },
    {
      number: "05",
      title: "Fittings & Refinement",
      icon: Ruler,
      description: "Return for fitting sessions where we perfect every detail. Your garment is adjusted and refined until the fit is flawless.",
      details: [
        "Basted fitting to check structure",
        "Forward fitting for final adjustments",
        "Minor tweaks made on the spot",
        "Typically 2-3 fitting sessions"
      ],
      image: "https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?w=800"
    },
    {
      number: "06",
      title: "Final Delivery",
      icon: Package,
      description: "Receive your finished masterpiece. We walk you through care instructions and celebrate the completion of your custom creation.",
      details: [
        "Final inspection and pressing",
        "Care instructions and garment bag included",
        "60-day complimentary alterations period",
        "Begin planning your next piece!"
      ],
      image: "https://images.pexels.com/photos/6764010/pexels-photo-6764010.jpeg?w=800"
    }
  ];

  const benefits = [
    { title: "Perfect Fit", description: "Tailored to your exact measurements" },
    { title: "Unique Style", description: "Every detail customized to you" },
    { title: "Premium Quality", description: "Finest fabrics and construction" },
    { title: "Expert Guidance", description: "Professional styling advice" },
    { title: "Lasting Value", description: "Investment pieces built to last" },
    { title: "Personal Service", description: "Dedicated consultant throughout" }
  ];

  return (
    <div data-testid="how-it-works-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 border-b border-white/5 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/6766243/pexels-photo-6766243.jpeg?w=1920')`
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">The Process</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
              How It Works
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto font-light text-lg mb-8">
              From consultation to delivery, experience the journey of creating 
              your perfect custom garment in six simple steps.
            </p>
            <Link to="/booking">
              <Button size="lg" className="group">
                Start Your Journey
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24 last:mb-0 ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-serif text-5xl text-primary/30">{step.number}</span>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon size={24} className="text-primary" />
                  </div>
                </div>
                
                <h2 className="font-serif text-3xl tracking-tight mb-4">
                  {step.title}
                </h2>
                
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {step.description}
                </p>
                
                <ul className="space-y-3">
                  {step.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 md:py-24 bg-card/30 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="mono-text text-primary mb-4">Why Choose Custom</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">
              The Benefits of Bespoke
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center p-6 border border-white/10 bg-card/50 hover:border-primary/30 transition-colors"
              >
                <h3 className="font-medium mb-2">{benefit.title}</h3>
                <p className="text-xs text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6">
              Ready to Begin?
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Your perfect garment awaits. Book your complimentary consultation 
              today and experience the art of custom tailoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button size="lg" className="w-full sm:w-auto">
                  Book Appointment
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  View FAQ
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
